import React, { useId } from 'react';

export interface WireDuctSvgProps {
  x: number;
  y: number;
  width: number;
  height: number;
  orientation?: 'horizontal' | 'vertical';
  designator?: string;
  name?: string;
  isSelected?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onRemove?: () => void;
}

/**
 * WireDuctSvg
 * Endüstriyel Pano İçi Delikli Plastik Kablo Kanalı (Slotted Cable Trunking / Pano Kanalı)
 * Standart DIN EN 50085 normunda, açık gri gövde, kablo giriş tırnakları (parmaklar)
 * ve geçmeli düz kapak yüzeyi ile gerçekçi görünüm sağlar.
 */
export const WireDuctSvg: React.FC<WireDuctSvgProps> = ({
  x,
  y,
  width,
  height,
  orientation = 'horizontal',
  designator = 'KANAL',
  name = 'Pano Kablo Kanalı',
  isSelected = false,
  onClick,
  onRemove,
}) => {
  const rawId = useId();
  const uid = rawId.replace(/[:]/g, '_');
  const isHorizontal = orientation === 'horizontal';

  return (
    <g
      id={`wire-duct-${designator.toLowerCase()}`}
      transform={`translate(${x}, ${y})`}
      style={{ cursor: 'pointer' }}
      onClick={onClick}
    >
      <title>{`${name} (${designator}) — Seçmek veya kaldırmak için tıklayın`}</title>

      <defs>
        {/* Gövde Degradesi */}
        <linearGradient
          id={`ductBodyGrad-${uid}`}
          x1="0%"
          y1="0%"
          x2={isHorizontal ? '0%' : '100%'}
          y2={isHorizontal ? '100%' : '0%'}
        >
          <stop offset="0%" stopColor="#475569" />
          <stop offset="20%" stopColor="#64748b" />
          <stop offset="50%" stopColor="#94a3b8" />
          <stop offset="80%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>

        {/* Kapak Degradesi */}
        <linearGradient
          id={`ductCoverGrad-${uid}`}
          x1="0%"
          y1="0%"
          x2={isHorizontal ? '0%' : '100%'}
          y2={isHorizontal ? '100%' : '0%'}
        >
          <stop offset="0%" stopColor="#94a3b8" />
          <stop offset="40%" stopColor="#cbd5e1" />
          <stop offset="70%" stopColor="#94a3b8" />
          <stop offset="100%" stopColor="#475569" />
        </linearGradient>

        {/* Hafif Alt Gölge */}
        <filter id={`ductShadow-${uid}`} x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="#090d16" floodOpacity="0.4" />
        </filter>
      </defs>

      {/* 1. Kanal Arka Taban Sacı / Yuvası */}
      <rect
        x="0"
        y="0"
        width={width}
        height={height}
        rx="2"
        fill="#1e293b"
        stroke="#0f172a"
        strokeWidth="1.2"
        filter={`url(#ductShadow-${uid})`}
      />

      {/* 2. Delikli Yan Taraklar / Parmaklar (Slots / Fingers) */}
      {isHorizontal ? (
        // Yatay Kanal Tarakları
        <g id="horizontal-comb-slots">
          {Array.from({ length: Math.max(1, Math.floor((width - 12) / 16)) }).map((_, i) => {
            const slotX = 6 + i * 16;
            return (
              <g key={`h-slot-${i}`}>
                {/* Üst tırnak deliği */}
                <rect x={slotX} y="1" width="5" height="7" rx="1" fill="#0f172a" />
                {/* Alt tırnak deliği */}
                <rect x={slotX} y={height - 8} width="5" height="7" rx="1" fill="#0f172a" />
              </g>
            );
          })}
        </g>
      ) : (
        // Dikey Kanal Tarakları
        <g id="vertical-comb-slots">
          {Array.from({ length: Math.max(1, Math.floor((height - 12) / 16)) }).map((_, i) => {
            const slotY = 6 + i * 16;
            return (
              <g key={`v-slot-${i}`}>
                {/* Sol tırnak deliği */}
                <rect x="1" y={slotY} width="7" height="5" rx="1" fill="#0f172a" />
                {/* Sağ tırnak deliği */}
                <rect x={width - 8} y={slotY} width="7" height="5" rx="1" fill="#0f172a" />
              </g>
            );
          })}
        </g>
      )}

      {/* 3. Kanal Geçmeli Kapağı (Snap-on Trunking Cover) */}
      {isHorizontal ? (
        <g id="horizontal-duct-cover">
          <rect
            x="3"
            y="6"
            width={width - 6}
            height={height - 12}
            rx="2"
            fill={`url(#ductCoverGrad-${uid})`}
            stroke="#475569"
            strokeWidth="0.8"
          />
          {/* Kapak Boyuna Pah ve Işık Çizgisi */}
          <line x1="4" y1="8" x2={width - 4} y2="8" stroke="#f1f5f9" strokeWidth="0.8" opacity="0.7" />
          <line x1="4" y1={height - 8} x2={width - 4} y2={height - 8} stroke="#334155" strokeWidth="0.8" />

          {/* Kanal Tanıtım Yazısı */}
          {width > 120 && (
            <text
              x={width / 2}
              y={height / 2 + 3}
              fill="#334155"
              fontSize="8"
              fontWeight="bold"
              fontFamily="monospace"
              letterSpacing="2"
              textAnchor="middle"
            >
              PVC CABLE DUCT
            </text>
          )}
        </g>
      ) : (
        <g id="vertical-duct-cover">
          <rect
            x="6"
            y="3"
            width={width - 12}
            height={height - 6}
            rx="2"
            fill={`url(#ductCoverGrad-${uid})`}
            stroke="#475569"
            strokeWidth="0.8"
          />
          <line x1="8" y1="4" x2="8" y2={height - 4} stroke="#f1f5f9" strokeWidth="0.8" opacity="0.7" />
          <line x1={width - 8} y1="4" x2={width - 8} y2={height - 4} stroke="#334155" strokeWidth="0.8" />

          {height > 120 && (
            <g transform={`translate(${width / 2 + 3}, ${height / 2}) rotate(90)`}>
              <text
                x="0"
                y="0"
                fill="#334155"
                fontSize="7.5"
                fontWeight="bold"
                fontFamily="monospace"
                letterSpacing="2"
                textAnchor="middle"
              >
                CABLE DUCT
              </text>
            </g>
          )}
        </g>
      )}

      {/* 4. Seçili Durum Vurgusu ve Kaldır Butonu */}
      {isSelected && (
        <g id="duct-selection-overlay">
          <rect
            x="-2"
            y="-2"
            width={width + 4}
            height={height + 4}
            rx="4"
            fill="none"
            stroke="#38bdf8"
            strokeWidth="2"
            strokeDasharray="4,3"
          />
          {onRemove && (
            <g
              transform={`translate(${width - 24}, -12)`}
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              style={{ cursor: 'pointer' }}
            >
              <circle cx="10" cy="10" r="10" fill="#ef4444" stroke="#ffffff" strokeWidth="1.5" />
              <text x="10" y="14" fill="#ffffff" fontSize="12" fontWeight="bold" textAnchor="middle">
                ×
              </text>
            </g>
          )}
        </g>
      )}
    </g>
  );
};
